import { useState } from "react";
import clsx from "clsx";
import type { BuffDefinition, StatusEffect } from "../types";
import { getBuffIconPath, isDebuff, resolveEffectMeta } from "../utils/buffs";

interface BuffBarProps {
  effects: StatusEffect[];
  definitions?: Record<string, BuffDefinition> | null;
  size?: "sm" | "md";
  className?: string;
}

export function BuffBar({ effects, definitions, size = "md", className }: BuffBarProps) {
  const [tooltip, setTooltip] = useState<{
    effect: StatusEffect;
    x: number;
    y: number;
  } | null>(null);

  if (!effects.length) return null;

  const iconSize = size === "sm" ? "w-7 h-7" : "w-8 h-8";

  return (
    <div className={clsx("relative", className)}>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {effects.map((effect) => {
          const meta = resolveEffectMeta(effect, definitions);
          const debuff = isDebuff(effect);
          return (
            <button
              key={`${effect.id}-${effect.tier}-${effect.stacks}-${effect.turns_left}`}
              type="button"
              className={clsx(
                "relative rounded-md bg-kingdom-dark/80 p-0.5 shrink-0",
                debuff ? "border-2 border-red-500" : "border-2 border-emerald-500",
              )}
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                setTooltip((prev) =>
                  prev?.effect.id === effect.id
                    ? null
                    : { effect, x: rect.left + rect.width / 2, y: rect.top },
                );
              }}
            >
              <img
                src={getBuffIconPath(meta.icon)}
                alt={meta.name}
                className={clsx(iconSize, "object-contain rounded")}
              />
              {effect.stacks > 1 && (
                <span className="absolute -bottom-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-black/80 text-[9px] font-bold text-white leading-[14px] text-center">
                  {effect.stacks}
                </span>
              )}
              {debuff && (
                <span className="absolute -top-1 -left-1 text-[10px] leading-none">🩸</span>
              )}
            </button>
          );
        })}
      </div>

      {tooltip && (
        <BuffTooltip
          effect={tooltip.effect}
          definitions={definitions}
          anchorX={tooltip.x}
          anchorY={tooltip.y}
          onClose={() => setTooltip(null)}
        />
      )}
    </div>
  );
}

function BuffTooltip({
  effect,
  definitions,
  anchorX,
  anchorY,
  onClose,
}: {
  effect: StatusEffect;
  definitions?: Record<string, BuffDefinition> | null;
  anchorX: number;
  anchorY: number;
  onClose: () => void;
}) {
  const meta = resolveEffectMeta(effect, definitions);
  const debuff = isDebuff(effect);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[60] cursor-default"
        aria-label="Close tooltip"
        onClick={onClose}
      />
      <div
        className={clsx(
          "fixed z-[70] max-w-[220px] -translate-x-1/2 -translate-y-full px-3 py-2 rounded-lg shadow-lg border text-left pointer-events-none",
          debuff
            ? "bg-red-950/95 border-red-600 text-red-100"
            : "bg-emerald-950/95 border-emerald-600 text-emerald-100",
        )}
        style={{ left: anchorX, top: anchorY - 8 }}
      >
        <div className="font-bold text-xs">{meta.name}</div>
        <div className="text-[10px] mt-0.5 opacity-90 leading-snug">{meta.description}</div>
      </div>
    </>
  );
}
